import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useDeviceFingerprint } from "@/hooks/monitoring/useDeviceFingerprint";
import { useTabFocusMonitoring } from "@/hooks/monitoring/useTabFocusMonitoring";
import { useBufferedMonitoring } from "@/hooks/monitoring/useBufferedMonitoring";
import { useClipboardMonitoring } from "@/hooks/monitoring/useClipboardMonitoring";
import { useWebcamMonitoring } from "@/hooks/monitoring/useWebcamMonitoring";
import { useIsMobile } from "@/hooks/use-mobile";

import { calculateIntegrityScore } from "@/utils/integrity-score";
import { isBlankResponse } from "@/utils/blank-response-check";
import { useTestData } from "@/hooks/useTestData";
import { TestContent } from "./TestContent";
import { WebcamSetupPage } from "./WebcamSetupPage";
import { TestStartScreen } from "./TestStartScreen";
import { AssessmentLayout } from "./AssessmentLayout";
import { BlankResponseWarningDialog } from "./BlankResponseWarningDialog";
import { ExitTestWarningDialog } from "./ExitTestWarningDialog";

import { TestCompletionDialog } from "./TestCompletionDialog";
import { TestSubmissionConfirmationDialog } from "./TestSubmissionConfirmationDialog";
import { MonitoringStatus } from "./MonitoringStatus";
import { WebcamMonitoringPreview } from "./WebcamMonitoringPreview";
import { DebugMonitoringInfo } from "./DebugMonitoringInfo";
import { UploadProgressDialog, MinimizedUploadIndicator } from "./UploadProgressDialog";
import { MonitoringEvent, MonitoringEventType } from "@/types/monitoring";
import { useUploadQueue } from "@/hooks/useUploadQueue";
import { uploadQueue } from "@/lib/uploadQueue";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle, Maximize, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { VideoRecorderHandle } from "./media/VideoRecorder";

interface TestTakerProps {
  testId: string;
  testResponseId: string;
  isPreview?: boolean;
}

export function TestTaker({ testId, testResponseId, isPreview }: TestTakerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [showWebcamSetup, setShowWebcamSetup] = useState(true); // Start with webcam setup or start screen
  const [showStartScreen, setShowStartScreen] = useState(false);
  const [monitoringActive, setMonitoringActive] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null); // Independent fetch for navigation
  const [webcamActive, setWebcamActive] = useState(false);
  const [warningCount, setWarningCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [monitoringEvents, setMonitoringEvents] = useState<MonitoringEvent[]>([]);
  const [checkingPreviousAttempts, setCheckingPreviousAttempts] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [showBlankResponseWarning, setShowBlankResponseWarning] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isVideoRecording, setIsVideoRecording] = useState(false);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [shouldNavigateAfterUpload, setShouldNavigateAfterUpload] = useState(false);
  const shouldNavigateAfterUploadRef = useRef(false);
  const [readyForVideoRecording, setReadyForVideoRecording] = useState(false); // NEW STATE
  const [lastUploadCompleteTime, setLastUploadCompleteTime] = useState<number | null>(null); // Safety fallback tracking
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string | undefined>(); // Audio device from setup
  const [isUploadDialogMinimized, setIsUploadDialogMinimized] = useState(false); // Minimize upload dialog

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number | null>(null);
  const [isLoadingIndex, setIsLoadingIndex] = useState(true);
  const [questionResponses, setQuestionResponses] = useState<Record<string, any>>({});
  const [isLoadingResponses, setIsLoadingResponses] = useState(true);
  const [isVerifyingResponse, setIsVerifyingResponse] = useState(true);
  const [pendingUploads, setPendingUploads] = useState<Map<string, { video: boolean; audio: boolean }>>(new Map());
  const [savedQuestionIds, setSavedQuestionIds] = useState<Set<string>>(new Set()); // Track which questions have been saved

  // Create ref for VideoRecorder control
  const videoRecorderRef = useRef<VideoRecorderHandle>(null);

  // Get upload queue state for progress tracking
  const { uploads } = useUploadQueue();

  // Calculate aggregate upload progress
  const uploadProgress = useMemo(() => {
    const activeUploads = uploads.filter(u => u.status === 'uploading' || u.status === 'queued');
    if (activeUploads.length === 0) return 100;
    
    const totalProgress = activeUploads.reduce((sum, u) => sum + (u.progress || 0), 0);
    return Math.round(totalProgress / activeUploads.length);
  }, [uploads]);

  const { 
    testDetails, 
    questions, 
    isLoadingTest, 
    isLoadingQuestions,
    testError,
    questionsError,
    isLoading,
    isError
  } = useTestData(testId);

  const currentQuestion = currentQuestionIndex !== null && questions ? 
    questions[currentQuestionIndex] : undefined;
  const currentResponse = currentQuestion ? 
    questionResponses[currentQuestion.id] : null;
  
  const { logEventBuffered, forceFlush } = useBufferedMonitoring(testResponseId);

   // Initialize webcam monitoring only if enabled for this test
   // Use strict equality to prevent race condition when testDetails is undefined
   const isWebcamMonitoringEnabled = testDetails?.webcam_monitoring_enabled === true;
   
   
    console.log('[TestTaker] Monitoring config:', { 
      isWebcamMonitoringEnabled, 
      webcamActive,
      showWebcamSetup,
      showStartScreen,
      testDetailsExists: !!testDetails,
      webcamMonitoringField: testDetails?.webcam_monitoring_enabled,
      testDetailsRaw: testDetails
    });
  
  const {
    stream: webcamStream,
    isCapturing,
    hasPermission,
    error: webcamError,
    isInitialized,
    isBackgroundRecording,
    monitoringMethod,
    initializeCamera,
    startCapture,
    stopCapture,
    cleanup: cleanupWebcam
  } = useWebcamMonitoring({
    testResponseId,
    currentQuestionId: currentQuestion?.id,
    isActive: webcamActive && isWebcamMonitoringEnabled
  });

  const handleEvent = useCallback(async (event: Omit<MonitoringEvent, 'test_response_id'>) => {
    await logEventBuffered({
      ...event,
      question_id: event.question_id || (currentQuestionIndex !== null && questions && questions[currentQuestionIndex] ? 
        questions[currentQuestionIndex]?.id : undefined)
    });
    
    if (event.severity > 1) {
      setWarningCount(prev => prev + 1);
    }
    
    setMonitoringEvents(prev => [...prev, { ...event, test_response_id: testResponseId }]);
  }, [logEventBuffered, testResponseId, currentQuestionIndex, questions]);

  useTabFocusMonitoring(handleEvent);
  useClipboardMonitoring(handleEvent, currentQuestion?.id);

  // Fetch organization_id independently for navigation (before testDetails loads)
  useEffect(() => {
    const fetchOrgId = async () => {
      if (!testId) return;
      try {
        const { data } = await supabase
          .from('tests')
          .select('organization_id')
          .eq('id', testId)
          .single();
        if (data?.organization_id) {
          setOrganizationId(data.organization_id);
        }
      } catch (err) {
        console.warn('[TestTaker] Failed to fetch organization_id:', err);
      }
    };
    fetchOrgId();
  }, [testId]);

  // Helper for context-aware error navigation
  const navigateToHome = useCallback(() => {
    // Use testDetails if available, otherwise use independently fetched organizationId
    const orgId = testDetails?.organization_id || organizationId;
    const isInnoventure = orgId === 'd4b5cd3c-483a-4574-9a20-ece44b857de6';
    if (isPreview && testId) {
      navigate(`/test-details/${testId}`);
    } else if (isInnoventure) {
      navigate('/innoventure');
    } else {
      navigate('/dashboard');
    }
  }, [testDetails?.organization_id, organizationId, isPreview, testId, navigate]);

  const requestFullscreen = useCallback(() => {
    const docEl = document.documentElement;
    
    if (docEl.requestFullscreen) {
      docEl.requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(err => console.error("Error attempting to enable fullscreen:", err));
    } else if ((docEl as any).mozRequestFullScreen) {
      (docEl as any).mozRequestFullScreen()
        .then(() => setIsFullscreen(true))
        .catch(err => console.error("Error attempting to enable fullscreen:", err));
    } else if ((docEl as any).webkitRequestFullscreen) {
      (docEl as any).webkitRequestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(err => console.error("Error attempting to enable fullscreen:", err));
    } else if ((docEl as any).msRequestFullscreen) {
      (docEl as any).msRequestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(err => console.error("Error attempting to enable fullscreen:", err));
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!showStartScreen && !showWebcamSetup && monitoringActive && !isFullscreen) {
      requestFullscreen();
    }
  }, [showStartScreen, showWebcamSetup, monitoringActive, isFullscreen, requestFullscreen]);

  // Handle mobile camera conflict: pause monitoring only when recording starts, not when question loads
  useEffect(() => {
    const isVideoQuestion = currentQuestion?.question_type === 'video_response';
    
    if (isMobile && isVideoQuestion) {
      // Case: Mobile on a video question
      if (isVideoRecording) {
        // Only pause monitoring when actually recording
        console.log('[TestTaker] Mobile: Pausing monitoring during active recording');
        setWebcamActive(false);
      }
      // Always mark recorder as ready (but monitoring stays active until recording starts)
      setReadyForVideoRecording(true);
    } else {
      // Case: Desktop OR non-video question
      setReadyForVideoRecording(false); // Unmount recorder (if not active question)
      
      // Restore monitoring if it was supposed to be on
      if (isWebcamMonitoringEnabled && !webcamActive && isInitialized && !showStartScreen && !showWebcamSetup) {
        console.log('[TestTaker] Restoring monitoring');
        setWebcamActive(true);
      }
    }
  }, [currentQuestion?.id, isMobile, isWebcamMonitoringEnabled, webcamActive, isInitialized, showStartScreen, showWebcamSetup, isVideoRecording]);

  // Callbacks for video recording state - simplified since VideoRecorder handles the delay
  const handleVideoRecordingStart = useCallback(() => {
    console.log('[TestTaker] Video recording starting - pausing monitoring');
    
    if (isMobile && webcamActive) {
      // Release monitoring camera - VideoRecorder will wait before acquiring
      setWebcamActive(false);
    }
    
    setIsVideoRecording(true);
  }, [isMobile, webcamActive]);

  const handleVideoRecordingStop = useCallback(() => {
    console.log('[TestTaker] Video recording stopped - restoring monitoring');
    setIsVideoRecording(false);
    
    // Restore monitoring after recording stops (on mobile)
    if (isMobile && isWebcamMonitoringEnabled) {
      setTimeout(() => {
        console.log('[TestTaker] Restoring monitoring after recording');
        setWebcamActive(true);
      }, 500);
    }
  }, [isMobile, isWebcamMonitoringEnabled]);

  // Proactive session refresh during video questions to prevent upload failures
  useEffect(() => {
    if (currentQuestion?.question_type === 'video_response' && !showStartScreen && !showWebcamSetup) {
      const refreshInterval = setInterval(async () => {
        // Skip refresh during active uploads to avoid any potential interference
        if (!isUploadInProgress) {
          console.log('[TestTaker] Proactive session refresh for video question');
          try {
            await supabase.auth.refreshSession();
          } catch (err) {
            console.warn('[TestTaker] Session refresh failed:', err);
          }
        }
      }, 5 * 60 * 1000); // Refresh every 5 minutes
      
      return () => clearInterval(refreshInterval);
    }
  }, [currentQuestion?.question_type, showStartScreen, showWebcamSetup, isUploadInProgress]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialState = async () => {
      if (!testResponseId || !isMounted) return;

      setIsLoadingIndex(true);
      try {
        console.log(`Loading last question index for response: ${testResponseId}`);
        const { data: responseData, error: indexError } = await supabase
          .from('test_responses')
          .select('last_question_index')
          .eq('id', testResponseId)
          .single();

        if (indexError) {
          console.error("Error loading last question index:", indexError);
          throw indexError;
        }
        
        console.log(`Last question index loaded:`, responseData?.last_question_index);
        if (isMounted) setCurrentQuestionIndex(responseData?.last_question_index ?? 0);
      } catch (err) {
        console.error("Error loading last question index:", err);
        if (isMounted) setCurrentQuestionIndex(0);
        toast({ title: "Error", description: "Could not resume test progress.", variant: "destructive" });
      } finally {
        if (isMounted) setIsLoadingIndex(false);
      }

      setIsLoadingResponses(true);
      try {
        console.log(`Loading previous responses for test response: ${testResponseId}`);
        const { data: responsesData, error: responsesError } = await supabase
          .from('question_responses')
          .select('question_id, response_content')
          .eq('test_response_id', testResponseId);

        if (responsesError) {
          console.error("Error loading question responses:", responsesError);
          throw responsesError;
        }

        console.log(`Loaded ${responsesData.length} previous responses`);
        const initialResponses = responsesData.reduce((acc, cur) => {
          acc[cur.question_id] = cur.response_content;
          return acc;
        }, {} as Record<string, any>);

        if (isMounted) setQuestionResponses(initialResponses);
      } catch (err) {
        console.error("Error loading initial responses:", err);
         toast({ title: "Error", description: "Could not load previous answers.", variant: "destructive" });
      } finally {
        if (isMounted) setIsLoadingResponses(false);
      }
    };

    loadInitialState();

    return () => { isMounted = false };
  }, [testResponseId, toast]);

  // Handle recovered uploads that need database save
  useEffect(() => {
    const handleRecoveredUpload = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { questionId, testResponseId: uploadTestResponseId, response } = customEvent.detail;

      // Only process if this is for our test response
      if (uploadTestResponseId !== testResponseId) return;

      console.log('[TestTaker] Processing recovered upload for question:', questionId);

      try {
        // Save to database
        await saveQuestionResponse(questionId, response);

        // Mark as saved to clean up IndexedDB
        if (response.uploadId) {
          await uploadQueue.markAsSaved(response.uploadId);
        }

        // Update local state
        setQuestionResponses(prev => ({
          ...prev,
          [questionId]: response
        }));
        setSavedQuestionIds(prev => new Set(prev).add(questionId));

        console.log('[TestTaker] Recovered upload saved successfully');
      } catch (err) {
        console.error('[TestTaker] Failed to save recovered upload:', err);
      }
    };

    window.addEventListener('recoveredUploadNeedsSave', handleRecoveredUpload);
    return () => {
      window.removeEventListener('recoveredUploadNeedsSave', handleRecoveredUpload);
    };
  }, [testResponseId]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Force cleanup webcam before page unload
      console.log('[TestTaker] Page unloading, forcing webcam cleanup');
      cleanupWebcam();
      
      const message = "Are you sure you want to leave? Your test progress may be lost.";
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    const handlePageHide = () => {
      // Additional cleanup on pagehide (covers more scenarios than beforeunload)
      console.log('[TestTaker] Page hiding, forcing webcam cleanup');
      cleanupWebcam();
    };
    
    if (!showStartScreen && !showWebcamSetup && monitoringActive) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('pagehide', handlePageHide);
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [showStartScreen, showWebcamSetup, monitoringActive, cleanupWebcam]);

  // Enhanced logging for webcam monitoring state for debugging
  useEffect(() => {
    console.log('[TestTaker] Enhanced Webcam state:', {
      userRole: user?.user_metadata?.role,
      userEmail: user?.email,
      userId: user?.id,
      testResponseId,
      testId,
      webcamActive,
      hasStream: !!webcamStream,
      isInitialized,
      hasPermission,
      isCapturing,
      error: webcamError,
      isWebcamMonitoringEnabled,
      showWebcamSetup,
      showStartScreen,
      monitoringActive,
      streamId: webcamStream?.id,
      timestamp: new Date().toISOString()
    });
  }, [webcamActive, webcamStream, isInitialized, hasPermission, isCapturing, webcamError, user, testResponseId, testId, isWebcamMonitoringEnabled, showWebcamSetup, showStartScreen, monitoringActive]);

  useEffect(() => {
    const checkPreviousAttempts = async () => {
      if (!user || !testId) return;
      
      try {
        setCheckingPreviousAttempts(true);
        
        // First check if user is an employer or collaborator
        const [testCreatorQuery, collaboratorQuery] = await Promise.all([
          supabase
            .from('tests')
            .select('creator_id')
            .eq('id', testId)
            .single(),
          supabase
            .from('test_collaborators')
            .select('id')
            .eq('test_id', testId)
            .eq('collaborator_id', user.id)
            .eq('status', 'accepted')
            .maybeSingle()
        ]);

        const isCreator = testCreatorQuery.data?.creator_id === user.id;
        const isCollaborator = !!collaboratorQuery.data;
        
        // If user is creator or collaborator, they can preview unlimited times
        if (isCreator || isCollaborator) {
          console.log('[TestTaker] User is creator or collaborator, allowing unlimited previews');
          return;
        }
        
        // For regular candidates, check for previous completed attempts
        const { data: previousResponses, error } = await supabase
          .from('test_responses')
          .select('id, status')
          .eq('test_id', testId)
          .eq('candidate_id', user.id)
          .eq('status', 'completed');
          
        if (error) throw error;
        
        if (previousResponses && previousResponses.length > 0) {
          toast({
            title: "Test Already Completed",
            description: "You have already taken this test. You cannot take it again.",
            variant: "destructive",
          });
          navigateToHome();
        }
      } catch (error: any) {
        console.error('Error checking previous attempts:', error);
        toast({
          title: "Error",
          description: "There was an error checking your previous attempts.",
          variant: "destructive",
        });
      } finally {
        setCheckingPreviousAttempts(false);
      }
    };
    
    checkPreviousAttempts();
  }, [user, testId, toast, navigate]);

  useEffect(() => {
    const verifyTestResponse = async () => {
      setIsVerifyingResponse(true);
      try {
        console.log(`Verifying test response status: ${testResponseId}`);
        const { data: testResponse, error } = await supabase
          .from('test_responses')
          .select(`
            status,
            tests!inner (
              scheduled_end_time
            )
          `)
          .eq('id', testResponseId)
          .single();

        if (error) {
          console.error('Error verifying test response:', error);
          toast({
            title: "Error",
            description: "Unable to verify test status. Please try again.",
            variant: "destructive",
          });
          navigateToHome();
          return;
        }

        if (testResponse.status !== 'in_progress') {
          toast({
            title: "Test Unavailable",
            description: testResponse.status === 'completed' 
              ? "This test has already been completed."
              : "This test session is no longer valid.",
            variant: "destructive",
          });
          navigateToHome();
          return;
        }

        // Check if scheduled_end_time has passed
        if (testResponse.tests?.scheduled_end_time) {
          const endTime = new Date(testResponse.tests.scheduled_end_time);
          const now = new Date();
          
          if (now > endTime) {
            console.log('[TestTaker] Test window has closed');
            toast({
              title: "Test Window Closed",
              description: "This test window has closed and can no longer be resumed.",
              variant: "destructive",
            });
            navigateToHome();
            return;
          }
        }
      } catch (error) {
        console.error('Error in verifyTestResponse:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred.",
          variant: "destructive",
        });
      } finally {
        setIsVerifyingResponse(false);
      }
    };

    verifyTestResponse();
  }, [testResponseId, navigate, toast]);

  const saveQuestionResponse = async (questionId: string, responseContent: any) => {
    try {
      // EMERGENCY FIX: Skip DB save if clearing for re-record (null response)
      if (responseContent === null) {
        console.log('[TestTaker] Response cleared for re-recording, skipping DB save');
        return;
      }
      
    // Normalize storage format to match legacy:
    // - Store video path string in response_content
    // - Store audio path string (if any) in audio_url
    // Handle both object format (from VideoRecorder) and string format (legacy)
    const isVideoObject = responseContent && typeof responseContent === 'object' && ('videoPath' in responseContent || 'videoUrl' in responseContent);
    const videoUrl = isVideoObject
      ? ((responseContent as any).videoPath || (responseContent as any).videoUrl)
      : responseContent;
    const audioUrl = isVideoObject
      ? ((responseContent as any).audioPath || (responseContent as any).audioUrl || null)
      : null;

      // Detect question type for processing flags
      const qType = questions?.find(q => q.id === questionId)?.question_type;
      const needsTranscription = qType === 'audio_response' || qType === 'video_response';

      const { data: upserted, error } = await supabase
        .from('question_responses')
        .upsert(
          {
            test_response_id: testResponseId,
            question_id: questionId,
            response_content: videoUrl,
            ...(audioUrl !== null ? { audio_url: audioUrl } : {}),
            evaluation_status: 'pending',
            ...(needsTranscription ? { transcription_status: 'pending' } : {}),
          } as any,
          {
            onConflict: 'test_response_id,question_id',
          }
        )
        .select('id')
        .single();

      if (error) throw error;
      
      // Transcription is handled by evaluate-test-responses when test completes
    } catch (error: any) {
      console.error('Error saving question response:', error);
      toast({
        title: "Error",
        description: "Failed to save your answer. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResponseChange = async (newResponse: any) => {
    if (!currentQuestion) return;

    // If response is being cleared (re-record), remove from saved set
    if (newResponse === null || newResponse === undefined) {
      setSavedQuestionIds(prev => {
        const next = new Set(prev);
        next.delete(currentQuestion.id);
        return next;
      });
    }

    setQuestionResponses(prev => ({
      ...prev,
      [currentQuestion.id]: newResponse
    }));

    // Check if this is a video/audio response - those are saved atomically in uploadQueue
    const isVideoAudioType = currentQuestion.question_type === 'video_response' || currentQuestion.question_type === 'audio_response';
    const hasUploadId = typeof newResponse === 'object' && newResponse?.uploadId;
    const isRecordingMarker = typeof newResponse === 'object' && newResponse?.recordingComplete;
    
    if (isVideoAudioType && isRecordingMarker) {
      // Just a marker indicating recording exists but upload pending - don't save to DB
      console.log('[TestTaker] Recording marker received - upload pending');
      return;
    }
    
    if (isVideoAudioType && hasUploadId) {
      // Video/audio: DB save happens atomically in uploadQueue.ts after storage upload
      // Just mark as saved for UI purposes - actual save is done by upload queue
      console.log('[TestTaker] Video/audio response - DB save handled by upload queue');
      setSavedQuestionIds(prev => new Set(prev).add(currentQuestion.id));
      
      // Clean up IndexedDB after upload queue completes (will be handled by queue)
      console.log('[TestTaker] Upload ID for tracking:', newResponse.uploadId);
    } else if (newResponse !== null && newResponse !== undefined) {
      // Non-video responses: save to DB here
      await saveQuestionResponse(currentQuestion.id, newResponse);
      setSavedQuestionIds(prev => new Set(prev).add(currentQuestion.id));
      console.log('[TestTaker] Non-video response saved to database');
    }
  };

  const updateLastQuestionIndex = async (index: number) => {
    if (!testResponseId) return;
    try {
      console.log(`Updating last_question_index to ${index} for response ${testResponseId}`);
      const { error } = await supabase
        .from('test_responses')
        .update({ last_question_index: index })
        .eq('id', testResponseId);
      if (error) throw error;
      console.log(`Successfully updated last_question_index to ${index}`);
    } catch (err) {
      console.error("Error saving last question index:", err);
      toast({ title: "Warning", description: "Failed to save current question progress.", variant: "destructive" });
    }
  };

  const handleCompleteTest = async () => {
    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      console.log('[TestTaker] Test completion already in progress, ignoring duplicate call');
      return;
    }
    
    try {
      setIsSubmitting(true);
      console.log('[TestTaker] Starting test completion process...');
      
      console.log('[TestTaker] Forcing flush of monitoring events...');
      await forceFlush();
      console.log('[TestTaker] Monitoring events flushed.');
      
      console.log('[TestTaker] Fetching all monitoring events from DB for score calculation...');
      const { data: events, error: eventsError } = await supabase
        .from('test_monitoring_events')
        .select('*')
        .eq('test_response_id', testResponseId);

      if (eventsError) {
        console.error('[TestTaker] Error fetching monitoring events for final calculation:', eventsError);
        toast({
          title: "Warning", 
          description: "Could not retrieve all monitoring data for final integrity score.", 
          variant: "warning"
        });
      }
      console.log(`[TestTaker] Fetched ${events?.length || 0} events for final calculation.`);

      const validEvents = events?.filter(event => {
        const validTypes: MonitoringEventType[] = [
          'tab_switch',
          'window_blur',
          'timing_anomaly',
          'ai_content',
          'content_similarity',
          'device_location',
          'clipboard_action'
        ];
        return validTypes.includes(event.event_type as MonitoringEventType);
      }) as MonitoringEvent[];

      const finalIntegrityScore = calculateIntegrityScore(validEvents || []);
      console.log(`[TestTaker] Calculated final integrity score: ${finalIntegrityScore}`);

      const { data: testResponse, error: checkError } = await supabase
        .from('test_responses')
        .select('status, id')
        .eq('id', testResponseId)
        .single();

      if (checkError) {
        console.error('Error checking test response:', checkError);
        throw checkError;
      }

      if (testResponse?.status !== 'in_progress') {
        toast({
          title: "Error",
          description: "This test session is no longer valid.",
          variant: "destructive",
        });
        navigateToHome();
        return;
      }

      // Cleanup webcam monitoring before completing test
      if (isWebcamMonitoringEnabled && webcamActive) {
        console.log('[TestTaker] Cleaning up webcam monitoring...');
        try {
          await Promise.race([
            cleanupWebcam(),
            new Promise<void>((resolve) => {
              setTimeout(() => {
                console.warn('[TestTaker] Webcam cleanup timeout - proceeding with test completion');
                resolve();
              }, 5000);
            })
          ]);
          console.log('[TestTaker] Webcam cleanup completed successfully');
        } catch (cleanupError) {
          console.error('[TestTaker] Error during webcam cleanup:', cleanupError);
          // Continue with test completion even if cleanup fails
        }
        setWebcamActive(false);
      }

      const { error: updateError } = await supabase
        .from('test_responses')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString(),
          integrity_score: finalIntegrityScore,
          last_question_index: null
        })
        .eq('id', testResponseId)
        .eq('status', 'in_progress');

      if (updateError) {
        if (updateError.code === '23505' || updateError.code === 'PGRST116' || updateError.message?.includes('Conflict')) {
          console.log('Test already marked as completed (conflict detected)');
          toast({
            title: "Test already submitted",
            description: "Your test has already been submitted successfully.",
          });
          
          // Check if test belongs to Innoventure organization
          const isIntelligencePlus = (testDetails as any)?.organization_id === 'd4b5cd3c-483a-4574-9a20-ece44b857de6';
          const successRoute = isIntelligencePlus ? '/innoventure-test-success' : '/test-submission-success';
          
          navigate(successRoute, {
            state: {
              testId: testId,
              testTitle: testDetails?.title,
              completedAt: new Date().toISOString(),
              employerMessage: (testDetails as any)?.completion_message,
              employerOrganization: (testDetails as any)?.employer?.company_name || (testDetails as any)?.organization?.organization
            }
          });
          return;
        }
        
        console.error('Error updating test response status:', updateError);
        throw updateError;
      }

      const triggerEvaluation = async (testResponseId: string) => {
        console.log('Triggering evaluation for test response:', testResponseId);
        try {
          const { data: evalData, error: evalError } = await supabase.functions.invoke("evaluate-test-responses", {
            body: { 
              testResponseId,
              timestamp: new Date().toISOString(),
              attemptId: Math.random().toString(36).substring(7)
            },
          });

          if (evalError) {
            console.error("Error triggering evaluation:", evalError);
            return false;
          }
          
          console.log('Evaluation triggered successfully:', evalData);
          return true;
        } catch (error) {
          console.error('Error invoking evaluation function:', error);
          return false;
        }
      };

      setSubmitAttempts(prev => prev + 1);
      const evaluationSuccess = await triggerEvaluation(testResponse.id);
      
      if (evaluationSuccess) {
        toast({
          title: "Test completed",
          description: "Your responses have been submitted successfully.",
        });
      } else {
        toast({
          title: "Test submitted",
          description: "Your test has been submitted. Evaluation will be processed in the background.",
        });
      }

      // Clear pending invite tokens after successful test completion
      sessionStorage.removeItem('pendingTestInviteToken');
      sessionStorage.removeItem('pendingTestId');
      sessionStorage.removeItem('redirectToJobPage');
      try {
        localStorage.removeItem('pendingTestInviteToken');
        localStorage.removeItem('pendingTestId');
        localStorage.removeItem('redirectToJobPage');
      } catch {}

      // Check if test belongs to Innoventure organization
      const isIntelligencePlus = (testDetails as any)?.organization_id === 'd4b5cd3c-483a-4574-9a20-ece44b857de6';
      const successRoute = isIntelligencePlus ? '/innoventure-test-success' : '/test-submission-success';
      
      navigate(successRoute, {
        state: {
          testId: testId,
          testTitle: testDetails?.title,
          completedAt: new Date().toISOString(),
          employerMessage: (testDetails as any)?.completion_message,
          employerOrganization: (testDetails as any)?.employer?.company_name || (testDetails as any)?.organization?.organization
        }
      });
    } catch (error: any) {
      console.error('Error completing test:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete the test. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMediaUploadStateChange = useCallback((uploading: boolean) => {
    setIsMediaUploading(uploading);
  }, []);

  const [failedUploads, setFailedUploads] = useState<Map<string, { video: boolean; audio: boolean }>>(new Map());

  const proceedToNextQuestion = useCallback(async () => {
    console.log('[TestTaker] Navigation starting, currentQuestion:', currentQuestion?.id);
    if (!currentQuestion || currentQuestionIndex === null) return;
    
    const currentResponseContent = questionResponses[currentQuestion.id];
    // Only save if not already saved (prevents redundant save after video upload)
    if (currentResponseContent !== undefined && currentResponseContent !== null && !savedQuestionIds.has(currentQuestion.id)) {
      console.log('[TestTaker] Saving response during navigation (not previously saved)');
      await saveQuestionResponse(currentQuestion.id, currentResponseContent);
    } else if (savedQuestionIds.has(currentQuestion.id)) {
      console.log('[TestTaker] Skipping redundant save - response already saved');
    }

    // Reset upload states before navigating - new question should start clean
    setIsUploadInProgress(false);
    shouldNavigateAfterUploadRef.current = false;
    setShouldNavigateAfterUpload(false);

    if (currentQuestionIndex < (questions?.length || 0) - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      await updateLastQuestionIndex(nextIndex);
    } else {
      // Show confirmation dialog before completing test
      setShowSubmitConfirmation(true);
    }
  }, [currentQuestion, currentQuestionIndex, questionResponses, questions, saveQuestionResponse, updateLastQuestionIndex, savedQuestionIds]);


  const handleUploadProgress = useCallback((questionId: string, status: 'pending' | 'complete' | 'failed', type: 'video' | 'audio') => {
    console.log(`[TestTaker] Upload progress: ${questionId} - ${type} - ${status}`);
    
    if (status === 'failed') {
      // Track failures separately using functional update
      setFailedUploads(prev => {
        const newMap = new Map(prev);
        const questionStatus = newMap.get(questionId) || { video: false, audio: false };
        questionStatus[type] = true;
        newMap.set(questionId, questionStatus);
        return newMap;
      });
    }
    
    // Clear from failed uploads when upload succeeds (e.g., after retry or re-record)
    if (status === 'complete') {
      setFailedUploads(prev => {
        const newMap = new Map(prev);
        const questionStatus = newMap.get(questionId);
        if (questionStatus) {
          questionStatus[type] = false;
          // Remove entirely if both video and audio are cleared
          if (!questionStatus.video && !questionStatus.audio) {
            newMap.delete(questionId);
          } else {
            newMap.set(questionId, questionStatus);
          }
        }
        return newMap;
      });
    }
    
    // Handle upload start - set blocking mode immediately
    if (status === 'pending') {
      console.log('[TestTaker] Upload started - entering blocking mode');
      setIsUploadInProgress(true);
      setLastUploadCompleteTime(null);
    }
    
    // Use functional update to avoid stale closure
    setPendingUploads(prev => {
      const newMap = new Map(prev);
      const questionStatus = newMap.get(questionId) || { video: false, audio: false };
      
      if (status === 'pending') {
        questionStatus[type] = true;
      } else {
        questionStatus[type] = false;
      }
      
      // Check if all uploads for this question are complete
      const hasMoreUploads = questionStatus.video || questionStatus.audio;
      
      if (!hasMoreUploads) {
        newMap.delete(questionId);
      } else {
        newMap.set(questionId, questionStatus);
      }
      
      return newMap;
    });
  }, []); // Empty dependency array - all state updates use functional form

  // Separate useEffect to manage isUploadInProgress based on pendingUploads - fixes race condition
  useEffect(() => {
    let anyUploadsPending = false;
    pendingUploads.forEach(status => {
      if (status.video || status.audio) {
        anyUploadsPending = true;
      }
    });
    
    console.log('[TestTaker] Checking pending uploads:', {
      anyUploadsPending,
      pendingUploadsCount: pendingUploads.size,
      isUploadInProgress,
      shouldNavigate: shouldNavigateAfterUploadRef.current
    });
    
    if (!anyUploadsPending && isUploadInProgress) {
      // All uploads complete - exit blocking mode
      console.log('[TestTaker] All uploads complete - exiting blocking mode');
      setIsUploadInProgress(false);
      setIsUploadDialogMinimized(false); // Reset minimized state
      setLastUploadCompleteTime(Date.now());
      
      // If we were waiting to navigate, do it now
      if (shouldNavigateAfterUploadRef.current) {
        shouldNavigateAfterUploadRef.current = false;
        setShouldNavigateAfterUpload(false);
        console.log('[TestTaker] Uploads complete, closing dialog before navigation');
        
        // Wait for React to re-render with closed dialog, then navigate
        setTimeout(() => {
          console.log('[TestTaker] Proceeding to next question after upload completion');
          proceedToNextQuestion();
        }, 100);
      }
    }

  }, [pendingUploads, isUploadInProgress, proceedToNextQuestion]);

  const totalPendingUploads = useMemo(() => {
    let count = 0;
    pendingUploads.forEach(status => {
      if (status.video) count++;
      if (status.audio) count++;
    });
    return count;
  }, [pendingUploads]);

  const totalFailedUploads = useMemo(() => {
    let count = 0;
    failedUploads.forEach(status => {
      if (status.video) count++;
      if (status.audio) count++;
    });
    return count;
  }, [failedUploads]);


  const handleNextQuestion = useCallback(async (fromTimer: boolean = false) => {
    console.log('[TestTaker] handleNextQuestion called', {
      fromTimer,
      currentQuestionIndex,
      currentQuestionId: currentQuestion?.id,
      currentQuestionType: currentQuestion?.question_type,
      isSubmitting,
      isUploadInProgress,
      pendingUploadsCount: pendingUploads.size,
      isRecording: videoRecorderRef.current?.isRecording?.() ?? 'N/A',
      lastUploadCompleteTime,
      timeSinceLastUpload: lastUploadCompleteTime ? Date.now() - lastUploadCompleteTime : null
    });

    if (currentQuestionIndex === null) {
      console.log('[TestTaker] RETURNING: currentQuestionIndex is null');
      return;
    }
    if (!currentQuestion) {
      console.log('[TestTaker] RETURNING: currentQuestion is undefined');
      return;
    }

    // Prevent multiple clicks
    if (isSubmitting) {
      console.log('[TestTaker] RETURNING: Already processing (isSubmitting=true)');
      return;
    }

    // Show toast for auto-submit due to timer expiry
    if (fromTimer) {
      toast({
        title: "Time limit reached",
        description: "Automatically submitting your response.",
        variant: "warning",
      });
    }

    // Safety fallback: If upload was marked complete more than 5 seconds ago but isUploadInProgress is still true
    if (isUploadInProgress && lastUploadCompleteTime && Date.now() - lastUploadCompleteTime > 5000) {
      console.log('[TestTaker] Safety fallback triggered - forcing isUploadInProgress to false');
      setIsUploadInProgress(false);
      setLastUploadCompleteTime(null);
      // Continue with navigation
    } else if (isUploadInProgress) {
      // If upload is already in progress, mark that we want to navigate after it completes
      console.log('[TestTaker] Upload in progress, will navigate after completion');
      shouldNavigateAfterUploadRef.current = true;
      setShouldNavigateAfterUpload(true);
      return;
    }

    // Check if video is currently recording (timer expiry case)
    if (
      currentQuestion.question_type === 'video_response' &&
      videoRecorderRef.current?.isRecording()
    ) {
      console.log('[TestTaker] Timer expired during recording, stopping and uploading');
      shouldNavigateAfterUploadRef.current = true;
      setShouldNavigateAfterUpload(true);
      try {
        await videoRecorderRef.current.stopRecordingAndUpload();
        console.log('[TestTaker] Video recording stopped and upload initiated');
        // Upload progress callbacks will handle navigation
      } catch (error) {
        console.error('[TestTaker] Error stopping video recording:', error);
        toast({
          title: "Warning",
          description: "There was an issue saving your video recording.",
          variant: "destructive",
        });
        shouldNavigateAfterUploadRef.current = false;
        setShouldNavigateAfterUpload(false);
        setIsUploadInProgress(false);
      }
      return;
    }

    // Check for blank response (only if NOT triggered by timer - timer auto-submits regardless)
    if (!fromTimer) {
      const currentResponseContent = questionResponses[currentQuestion.id];
      if (isBlankResponse(currentQuestion.question_type, currentResponseContent)) {
        console.log('[TestTaker] Blank response detected, showing warning dialog');
        setShowBlankResponseWarning(true);
        return;
      }
    }

    // If already uploaded or no media, proceed immediately
    console.log('[TestTaker] Proceeding to next question immediately');
    await proceedToNextQuestion();
  }, [currentQuestionIndex, currentQuestion, isSubmitting, isUploadInProgress, pendingUploads.size, lastUploadCompleteTime, proceedToNextQuestion, toast, questionResponses]);

  const handleConfirmSubmit = async () => {
    setShowSubmitConfirmation(false);
    // Set isSubmitting BEFORE calling handleCompleteTest to prevent race condition
    setIsSubmitting(true);
    await handleCompleteTest();
  };

  const handleCancelSubmit = () => {
    setShowSubmitConfirmation(false);
  };

  const handleConfirmBlankSubmit = useCallback(async () => {
    setShowBlankResponseWarning(false);
    await proceedToNextQuestion();
  }, [proceedToNextQuestion]);

  const handleCancelBlankSubmit = () => {
    setShowBlankResponseWarning(false);
  };

  const handleExitTest = useCallback(async () => {
    if (isExiting) return;
    
    try {
      setIsExiting(true);
      console.log('[TestTaker] Exiting test - marking as abandoned...');
      
      // Flush any pending monitoring events
      await forceFlush();
      
      // Update test response status to abandoned
      const { error } = await supabase
        .from('test_responses')
        .update({ 
          status: 'abandoned',
          completed_at: new Date().toISOString()
        })
        .eq('id', testResponseId);
      
      if (error) {
        console.error('[TestTaker] Error marking test as abandoned:', error);
        throw error;
      }
      
      console.log('[TestTaker] Test marked as abandoned successfully');
      
      // Cleanup webcam
      cleanupWebcam();
      
      toast({
        title: "Test Abandoned",
        description: "Your responses up to the last saved question have been preserved.",
      });
      
      // Navigate based on context
      const isInnoventure = testDetails?.organization_id === 'd4b5cd3c-483a-4574-9a20-ece44b857de6';
      if (isInnoventure) {
        navigate('/innoventure');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('[TestTaker] Error exiting test:', error);
      setIsExiting(false);
      toast({
        title: "Error",
        description: "Failed to exit test. Please try again.",
        variant: "destructive",
      });
    }
  }, [isExiting, forceFlush, testResponseId, cleanupWebcam, toast, navigate, testDetails?.organization_id]);

  const handleTimerExpiry = useCallback(() => {
    handleNextQuestion(true);
  }, [handleNextQuestion]);

  const handleCancelUpload = useCallback(() => {
    console.warn('[TestTaker] User canceled upload - will re-record');

    // Clear all upload-related state
    setIsUploadInProgress(false);
    setPendingUploads(new Map());
    setIsUploadDialogMinimized(false);
    setShouldNavigateAfterUpload(false);
    shouldNavigateAfterUploadRef.current = false;

    // Track as failed for current question
    if (currentQuestion?.id) {
      setFailedUploads(prev => {
        const newMap = new Map(prev);
        newMap.set(currentQuestion.id, { video: true, audio: true });
        return newMap;
      });
    }

    toast({
      title: "Upload Canceled",
      description: "You can now re-record your response.",
      variant: "destructive",
    });
  }, [currentQuestion?.id, toast]);

  const handleRetryUpload = useCallback(async () => {
    console.log('[TestTaker] User requested upload retry');
    
    if (!currentQuestion?.id) return;
    
    // Find the upload in the queue by question ID
    const upload = uploadQueue.getUploadByQuestionId(currentQuestion.id);
    
    if (upload) {
      console.log('[TestTaker] Found upload to retry:', upload.id, 'status:', upload.status);
      
      // Check if blobs still exist in IndexedDB before retrying
      const { getPendingUpload } = await import('@/lib/blobStorage');
      const pending = await getPendingUpload(upload.id);
      
      if (!pending || (!pending.videoBlob && !pending.audioBlob)) {
        console.warn('[TestTaker] No blobs found for retry - need to re-record');
        toast({
          title: "Cannot Retry",
          description: "Recording data was lost. Please re-record your response.",
          variant: "destructive",
        });
        // Clear the stale upload from queue
        uploadQueue.removeByQuestionId(currentQuestion.id);
        setIsUploadInProgress(false);
        setPendingUploads(new Map());
        return;
      }
      
      // Re-set pending state to show uploading again
      setPendingUploads(prev => {
        const newMap = new Map(prev);
        newMap.set(currentQuestion.id, { video: true, audio: true });
        return newMap;
      });
      setIsUploadInProgress(true);
      
      // Clear from failed uploads
      setFailedUploads(prev => {
        const newMap = new Map(prev);
        newMap.delete(currentQuestion.id);
        return newMap;
      });
      
      // Retry the upload
      uploadQueue.retry(upload.id);
      
      toast({
        title: "Retrying Upload",
        description: "Re-attempting to upload your recording...",
      });
    } else {
      console.warn('[TestTaker] No upload found in queue for question:', currentQuestion.id);
      toast({
        title: "Cannot Retry",
        description: "No pending upload found. Please re-record your response.",
        variant: "destructive",
      });
    }
  }, [currentQuestion?.id, toast]);

  const handleForceSubmit = useCallback(async () => {
    console.warn('[TestTaker] User forcing upload completion');

    if (!currentQuestion?.id) {
      console.error('[TestTaker] No current question for force submit');
      return;
    }

    // Find the upload in the queue
    const upload = uploadQueue.getUploadByQuestionId(currentQuestion.id);

    let savedToDb = false;
    let hasVideoPath = false;
    let hasAudioPath = false;

    if (upload) {
      // Force finalize the upload - save whatever we have
      const result = await uploadQueue.forceFinalize(upload.id);
      savedToDb = result.savedToDb;
      hasVideoPath = result.hasVideoPath;
      hasAudioPath = result.hasAudioPath;

      console.log('[TestTaker] Force finalize result:', result);

      // Update local tracking based on result
      if (savedToDb) {
        // Successfully saved - mark as saved in our tracking
        setSavedQuestionIds(prev => new Set(prev).add(currentQuestion.id));
        setFailedUploads(prev => {
          const newMap = new Map(prev);
          newMap.delete(currentQuestion.id);
          return newMap;
        });
      }
    }

    // Clear upload UI state
    setIsUploadInProgress(false);
    setPendingUploads(new Map());
    setIsUploadDialogMinimized(false);
    shouldNavigateAfterUploadRef.current = false;
    setShouldNavigateAfterUpload(false);

    // Show appropriate toast based on result
    if (savedToDb) {
      // At least one of video/audio saved = success
      const savedParts = [];
      if (hasVideoPath) savedParts.push('video');
      if (hasAudioPath) savedParts.push('audio');
      
      toast({
        title: "Response Saved",
        description: `Your ${savedParts.join(' and ')} response has been saved successfully.`,
      });
    } else if (!hasVideoPath && !hasAudioPath) {
      // Nothing was uploaded - blank response
      toast({
        title: "Blank Response Recorded",
        description: "No recording was uploaded. Moving to next question.",
      });
    } else {
      // Had paths but DB save failed (shouldn't happen often with retries)
      toast({
        title: "Save Issue",
        description: "Upload completed but database save failed. This may be recovered later.",
        variant: "destructive",
      });
    }

    // Navigate to next question
    setTimeout(() => {
      proceedToNextQuestion();
    }, 100);
  }, [currentQuestion?.id, toast, proceedToNextQuestion]);

   // handleAcceptWarning was removed - flow now starts directly with webcam setup or start screen


   const handleAcceptWebcam = async (audioDeviceId?: string) => {
     console.log('[TestTaker] Webcam setup accepted, initializing camera. Audio device:', audioDeviceId);
     setSelectedAudioDeviceId(audioDeviceId);
     setShowWebcamSetup(false);
     
     // CRITICAL FIX: Set webcamActive BEFORE initialization to prevent race condition
     // The cleanup effect in useWebcamMonitoring was firing because isActive was false
     // while isInitialized became true, causing immediate cleanup after init
     setWebcamActive(true);
     setShowStartScreen(true);
     setMonitoringActive(true);
     
      try {
        console.log('[TestTaker] Calling initializeCamera');
        await initializeCamera();
        console.log('[TestTaker] Camera initialization completed successfully');
      } catch (error) {
       console.error('[TestTaker] Error during webcam initialization:', error);
       toast({
         title: "Camera Error",
         description: "Failed to initialize camera. Please check your permissions and try again.",
         variant: "destructive",
       });
       // Reset states on error
       setWebcamActive(false);
       setShowWebcamSetup(true);
       setShowStartScreen(false);
       setMonitoringActive(false);
     }
   };

  const handleStartTest = () => {
    console.log('[TestTaker] Start button clicked, beginning test');
    setShowStartScreen(false);
  };

  const handleDeclineWebcam = () => {
    console.log('[TestTaker] Webcam setup declined, navigating away');
    setShowWebcamSetup(false);
    toast({
      title: "Test Cancelled",
      description: "Camera and microphone access is required for this test.",
      variant: "destructive",
    });
    
    // Navigate based on context - use testDetails if available, otherwise use independently fetched organizationId
    const orgId = testDetails?.organization_id || organizationId;
    const isInnoventure = orgId === 'd4b5cd3c-483a-4574-9a20-ece44b857de6';
    if (isPreview && testId) {
      navigate(`/test-details/${testId}`);
    } else if (isInnoventure && testId) {
      navigate(`/innoventure-test/${testId}`);
    } else {
      navigate('/dashboard');
    }
  };

  // Cleanup webcam on unmount - ALWAYS cleanup regardless of state
  useEffect(() => {
    return () => {
      console.log('[TestTaker] Component unmounting, forcing webcam cleanup');
      // Always cleanup on unmount, regardless of webcamActive state
      // The cleanup function handles no-op cases internally
      cleanupWebcam();
    };
  }, [cleanupWebcam]);
  
  // Camera/mic check page is always shown regardless of webcam monitoring setting
  // This ensures candidates can verify their devices work before starting any test

  if (showWebcamSetup) {
    return (
      <WebcamSetupPage 
        onAccept={handleAcceptWebcam}
        onDecline={handleDeclineWebcam}
        companyName={(testDetails as any)?.employer?.company_name || (testDetails as any)?.creator?.organization || "Company"}
        companyLogo={(testDetails as any)?.employer?.company_logo_url || (testDetails as any)?.creator?.company_logo_url}
      />
    );
  }

  if (showStartScreen) {
    return (
      <TestStartScreen
        testTitle={testDetails?.title || "Assessment"}
        questionCount={questions?.length || 0}
        employerOrganization={(testDetails as any)?.employer?.company_name || (testDetails as any)?.organization?.organization}
        employerMessage={(testDetails as any)?.welcome_message}
        testId={testId}
        organizationId={testDetails?.organization_id}
        isPreview={isPreview}
        onStart={handleStartTest}
      />
    );
  }


  if (checkingPreviousAttempts || isLoadingTest || isLoadingQuestions || isLoadingIndex || isLoadingResponses || isVerifyingResponse) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
         <div className="text-center">
           <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
           <p className="text-muted-foreground">Loading test...</p>
         </div>
       </div>
    );
  }

  if (isError || !questions || questions.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Error Loading Test</h2>
            <p className="text-muted-foreground mb-4">
              {testError?.message || questionsError?.message || "Could not load test questions or progress. Please try again."}
            </p>
            <Button onClick={navigateToHome}>Return Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentQuestionIndex === null) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Session Error</h2>
            <p className="text-muted-foreground mb-4">
              Unable to determine your progress in this test. This might be due to a session issue.
            </p>
            <Button onClick={navigateToHome}>Return Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AssessmentLayout 
      companyName={(testDetails as any)?.employer?.company_name || (testDetails as any)?.organization?.organization || "Company"}
      companyLogo={(testDetails as any)?.employer?.company_logo_url || (testDetails as any)?.company_logo_url}
    >
      <div className="flex-1 bg-background p-6 pb-0">
        <div className="fixed top-20 right-4 z-50 flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowExitConfirmation(true)}
            className="flex items-center gap-1 text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50 hover:bg-destructive/10"
          >
            <LogOut className="h-3.5 w-3.5" />
            Exit Test
          </Button>
          {!isFullscreen && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={requestFullscreen}
              className="flex items-center gap-1"
            >
              <Maximize className="h-3.5 w-3.5" />
              Fullscreen
            </Button>
          )}
        </div>
        <TestContent
          testId={testId}
          testResponseId={testResponseId}
          currentQuestion={currentQuestion}
          response={currentResponse}
          onResponseChange={handleResponseChange}
          onNext={() => handleNextQuestion(false)}
          onTimerExpiry={handleTimerExpiry}
          isLastQuestion={currentQuestionIndex === questions.length - 1}
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          testTitle={testDetails?.title || "Test"}
          videoRecorderRef={videoRecorderRef}
          isSubmitting={isSubmitting}
          isMediaUploading={isMediaUploading}
          onMediaUploadStateChange={handleMediaUploadStateChange}
          onVideoRecordingStart={handleVideoRecordingStart}
          onVideoRecordingStop={handleVideoRecordingStop}
          onUploadProgress={handleUploadProgress}
          isUploadInProgress={isUploadInProgress}
          hasPendingUploads={pendingUploads.size > 0}
          isVideoRecorderReady={!isMobile || readyForVideoRecording}
          audioDeviceId={selectedAudioDeviceId}
        />
        <MonitoringStatus 
          isActive={
            (!isWebcamMonitoringEnabled || (isWebcamMonitoringEnabled && webcamActive && isInitialized))
          } 
          warningCount={warningCount} 
        />
        <TestCompletionDialog open={isSubmitting} />
            <TestSubmissionConfirmationDialog
              open={showSubmitConfirmation}
              onConfirm={handleConfirmSubmit}
              onCancel={handleCancelSubmit}
              pendingUploadsCount={totalPendingUploads}
              failedUploadsCount={totalFailedUploads}
              hasVideoResponses={questions?.some(q => q.question_type === 'video_response')}
              onForceSubmit={handleForceSubmit}
            />
            <BlankResponseWarningDialog
              open={showBlankResponseWarning}
              onConfirm={handleConfirmBlankSubmit}
              onCancel={handleCancelBlankSubmit}
              questionType={currentQuestion?.question_type || 'essay'}
            />
            <ExitTestWarningDialog
              open={showExitConfirmation}
              onConfirm={handleExitTest}
              onCancel={() => setShowExitConfirmation(false)}
              isExiting={isExiting}
            />
            {isUploadDialogMinimized ? (
              <MinimizedUploadIndicator 
                progress={uploadProgress}
                onClick={() => setIsUploadDialogMinimized(false)}
              />
            ) : (
              <UploadProgressDialog
                open={isUploadInProgress}
                uploadStatus={{
                  video: currentQuestion ? (pendingUploads.get(currentQuestion.id)?.video || false) : false,
                  audio: currentQuestion ? (pendingUploads.get(currentQuestion.id)?.audio || false) : false,
                }}
                progress={uploadProgress}
                onMinimize={() => setIsUploadDialogMinimized(true)}
                onRetry={handleRetryUpload}
                onCancel={handleCancelUpload}
                onForceSubmit={handleForceSubmit}
              />
            )}
          {/* Show monitoring preview except when actively recording on mobile */}
          {(!isMobile || currentQuestion?.question_type !== 'video_response' || !isVideoRecording) && 
            webcamActive && isWebcamMonitoringEnabled && (
            <WebcamMonitoringPreview
              stream={webcamStream}
              isActive={webcamActive}
              isCapturing={isCapturing}
              isBackgroundRecording={isBackgroundRecording}
              monitoringMethod={monitoringMethod}
            />
          )}
      </div>
    </AssessmentLayout>
  );
}
